import { ref, reactive, computed } from 'vue'

/**
 * canEditTransaction
 * 
 * Determines if a transaction can be edited based on payment method.
 * Transactions with payment methods 5 or 8 are locked (read-only).
 * 
 * @param {Object} transaccion - The transaction object with metodo_pago property
 * @returns {boolean} - True if transaction can be edited, false otherwise
 */
export function canEditTransaction(transaccion) {
    return ![5, 8].includes(transaccion?.metodo_pago)
}

/**
 * useEditarTransaccion
 * 
 * Manages the business logic for editing a transaction.
 * Uses dependency injection for toast and completion callbacks, enabling clean separation
 * of concerns: composable handles orchestration (save → toast → callback), views handle only validation.
 * 
 * @param {Object} transaccion - The transaction object:
 *   { id, fecha, metodo_pago, ...rest }
 * @param {Object} options - Injected dependencies:
 *   @param {Object} options.store - Vuex store instance (required)
 *   @param {Object} options.config - App config with getVuexAlias() (required)
 *   @param {Array} options.excludedMetodoPagoIds - Payment method IDs to exclude (default: [5, 8])
 *   @param {Object} options.toast - Toast service with success(msg), error(msg) methods (default: no-op)
 *   @param {Function} options.onComplete - Callback after save: (success: boolean) => void (default: no-op)
 *   @param {string} options.vuexAlias - Vuex alias for the edit action (default: 'back.abonados.transacciones.editar')
 * @returns {Object}
 *   {
 *     localTransaccion: Reactive - local copy of transaccion with formatted fecha
 *     metodos_pago: ComputedRef<Array> - filtered payment methods (excludes ids 5, 8)
 *     loading: Ref<boolean> - loading state during dispatch
 *     submitTransaction: (params: Object) => Promise - orchestrates save → toast → callback
 *     formatDate: (dateString: string) => string - formats to YYYY-MM-DDTHH:mm
 *   }
 */
export function useEditarTransaccion(transaccion = {}, options = {}) {
    const { 
        store, 
        config, 
        excludedMetodoPagoIds = [5, 8],
        toast = { success: () => {}, error: () => {} },
        onComplete = () => {},
        vuexAlias = 'back.abonados.transacciones.editar'
    } = options

    // Validate required dependencies
    if (!store) throw new Error('useEditarTransaccion: store is required')
    if (!config) throw new Error('useEditarTransaccion: config is required')

    /**
     * Format ISO date string to datetime-local format (YYYY-MM-DDTHH:mm)
     * @param {string} dateString - ISO format date string
     * @returns {string} - Formatted date string
     */
    const formatDate = dateString => {
        const date = new Date(dateString)
        return date.toISOString().slice(0, 16)
    }

    /**
     * Local reactive copy of transaccion with formatted fecha
     * Used for two-way binding in the form
     */
    const localTransaccion = reactive({
        ...transaccion,
        fecha: transaccion.fecha ? formatDate(transaccion.fecha) : ''
    })

    /**
     * Computed property: filtered payment methods
     * Excludes payment method IDs specified in excludedMetodoPagoIds
     */
    const metodos_pago = computed(() => {
        const base = store.getters.getTeatro?.metodos_pago_abonos_bases || []
        return base.slice().filter(m => !excludedMetodoPagoIds.includes(m.id))
    })

    /**
     * Loading state ref
     * Set to true while dispatch is pending
     */
    const loading = ref(false)

    /**
     * Internal: Save transaction action
     * Dispatches the edit action with merged payload
     * Returns Promise that resolves/rejects with the API response
     * Does NOT perform side effects (toast, emit, navigation)
     * 
     * @param {Object} params - Additional parameters to merge into payload
     * @returns {Promise} - Resolves with API response or rejects with error
     */
    const saveTransaccion = async(params = {}) => {
        loading.value = true
        try {
            const payload = {
                ...params,
                transaccion_id: localTransaccion.id,
                fecha: localTransaccion.fecha,
                metodo_pago: localTransaccion.metodo_pago,
                descuento_aplicado: localTransaccion.descuento_aplicado ?? 0,
                descuento_integro: localTransaccion.descuento_integro ?? 0
            }
            return await store.dispatch(
                config.getVuexAlias(vuexAlias),
                payload
            )
        } finally {
            loading.value = false
        }
    }

    /**
     * Public API: Submit transaction with full orchestration
     * Handles save → toast notification → completion callback
     * Never throws; all errors handled internally
     * 
     * @param {Object} params - Additional parameters to merge into payload
     * @returns {Promise<void>}
     */
    const submitTransaction = async(params = {}) => {
        try {
            const res = await saveTransaccion(params)
            const message = res.data?.message || 'Transacción editada'
            toast.success(message)
            onComplete(true)
        } catch (err) {
            const message = err.response?.data?.message || 'Error al editar transacción'
            toast.error(message)
            onComplete(false)
        }
    }

    return {
        localTransaccion,
        metodos_pago,
        loading,
        saveTransaccion,  // @private — use submitTransaction for side-effect orchestration
        submitTransaction,
        formatDate
    }
}
